import React, { FC, ComponentProps } from 'react';
import ReactCalendar from 'react-calendar';
import dayjs from 'dayjs';
import classnames from 'classnames';
import {
  LgChevronLeftIcon as ChevronLeftIcon,
  LgChevronRightIcon as ChevronRightIcon,
} from '@stpkg/stand-react';

import { useIsMobile } from '../../hooks/helpers/useIsMobile';

export interface RSMonthCalendarProps {
  activeStartDate: NonNullable<ReactCalendar['props']['activeStartDate']>;
  onActiveStartDateChange: NonNullable<
    ReactCalendar['props']['onActiveStartDateChange']
  >;
  onClickDay: NonNullable<ReactCalendar['props']['onClickDay']>;
  // ISO8601でフォーマットされた日付を受け付ける
  selectableDates: string[];
  // ISO8601でフォーマットされた日付を受け付ける
  selectedDates: string[];
  locale?: ComponentProps<typeof ReactCalendar>['locale'];
  calendarType?: ComponentProps<typeof ReactCalendar>['calendarType'];
}

export const RSMonthCalendar: FC<RSMonthCalendarProps> = ({
  selectableDates,
  selectedDates,
  ...props
}) => {
  const isMobile = useIsMobile();

  const color = '#0066FF';

  return (
    <>
      <div
        className={classnames(
          'rs-font',
          'wrapper',
          'relative',
          !isMobile && 'pc',
        )}
      >
        <ReactCalendar
          {...props}
          maxDetail="month"
          minDetail="month"
          prevLabel={
            <ChevronLeftIcon width="20" height="20" viewBox="0 0 48 48" />
          }
          nextLabel={
            <ChevronRightIcon width="20" height="20" viewBox="0 0 48 48" />
          }
          prev2Label={null}
          next2Label={null}
          formatDay={(_locale, date) => {
            return dayjs(date).format('D');
          }}
          tileClassName={({ date, activeStartDate }) => {
            // 月が異なる場合は非表示にする
            if (!dayjs(activeStartDate).isSame(date, 'month')) {
              return ['invisible'];
            }

            const isSelected = selectedDates.some((selectedDate) => {
              return dayjs(selectedDate, 'YYYY-MM-DD').isSame(date, 'day');
            });

            return classnames([isSelected && 'selected']);
          }}
          tileDisabled={({ date }) => {
            return !selectableDates.some((selectableDate) => {
              return dayjs(selectableDate, 'YYYY-MM-DD').isSame(date, 'day');
            });
          }}
        />
      </div>
      <style jsx>{`
        .wrapper :global(.react-calendar__navigation) {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }
        .wrapper.pc :global(.react-calendar__navigation) {
          margin-bottom: 24px;
        }

        .wrapper :global(.react-calendar__navigation__label) {
          padding: 0;
          background-color: transparent;
          color: rgba(27, 27, 27, 1);
          font-weight: 600;
          font-size: 16px;
          line-height: 160%;
          border: none;
        }
        .wrapper.pc :global(.react-calendar__navigation__label) {
          font-size: 15px;
        }

        .wrapper :global(.react-calendar__month-view__weekdays__weekday) {
          margin-bottom: 4px;
          text-align: center;
        }
        .wrapper.pc :global(.react-calendar__month-view__weekdays__weekday) {
          margin-bottom: 8px;
        }
        .wrapper
          :global(.react-calendar__month-view__weekdays__weekday abbr[title]) {
          color: rgba(27, 27, 27, 1);
          font-size: 12px;
          line-height: 150%;
          font-weight: 400;
          text-decoration: none;
        }

        .wrapper :global(.react-calendar__navigation__arrow) {
          display: block;
          box-sizing: border-box;
          width: 24px;
          height: 24px;
          padding: 2px;
          background-color: transparent;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          outline: 2px solid transparent;
          transition-property: background-color outline-color;
          transition-duration: 200ms;
          transition-timing-function: cubic-bezier(0.6, 0, 0.6, 0.6);
        }
        .wrapper :global(.react-calendar__navigation__arrow svg) {
          fill: rgba(27, 27, 27, 1);
        }
        .wrapper :global(.react-calendar__navigation__arrow:focus-visible) {
          outline-color: ${color};
        }
        .wrapper
          :global(.react-calendar__navigation__arrow:hover:not(:disabled)) {
          background-color: rgba(244, 244, 244, 1);
        }
        .wrapper :global(.react-calendar__navigation__arrow:disabled) {
          cursor: default;
        }
        .wrapper :global(.react-calendar__navigation__arrow:disabled svg) {
          fill: rgba(205, 205, 205, 1);
        }

        .wrapper :global(.react-calendar__tile) {
          box-sizing: border-box;
          padding: 4px 0px;
          background-color: transparent;
          border: none;
          cursor: pointer;
          outlie: none;
        }
        .wrapper.pc :global(.react-calendar__tile) {
          padding: 8px 0px;
        }
        .wrapper :global(.react-calendar__tile:focus) {
          outline: none;
        }
        .wrapper :global(.react-calendar__tile:disabled) {
          cursor: default;
        }

        .wrapper :global(.react-calendar__tile abbr) {
          position: relative;
          display: inline-block;
          box-sizing: border-box;
          width: 36px;
          height: 36px;
          background-color: transparent;
          color: ${color};
          border: 1px solid ${color};
          border-radius: 50%;
          font-size: 13px;
          line-height: 36px;
          font-weight: 600;
          transition-property: color, background-color;
          transition-duration: 200ms;
          transition-timing-function: cubic-bezier(0.6, 0, 0.6, 0.6);
        }
        .wrapper :global(.react-calendar__tile abbr::before) {
          content: '';
          position: absolute;
          display: block;
          box-sizing: border-box;
          width: calc(100% + 8px);
          height: calc(100% + 8px);
          top: -4px;
          left: -4px;
          border: 2px solid transparent;
          border-radius: 50%;
          transition-property: border-color;
          transition-duration: 200ms;
          transition-timing-function: cubic-bezier(0.6, 0, 0.6, 0.6);
        }
        .wrapper.pc :global(.react-calendar__tile abbr) {
          font-size: 12px;
        }
        .wrapper :global(.react-calendar__tile.selected:not(:disabled) abbr),
        .wrapper :global(.react-calendar__tile:not(:disabled) abbr:hover) {
          background-color: ${color};
          color: #fff;
        }
        .wrapper :global(.react-calendar__tile:focus-visible abbr::before) {
          border-color: ${color};
        }
        .wrapper :global(.react-calendar__tile:disabled abbr) {
          color: rgba(205, 205, 205, 1);
          border-color: transparent;
        }
      `}</style>
    </>
  );
};
